import React, { useEffect, useState } from 'react';
import {
  getSubGroups,
  addSubGroup,
  updateSubGroup,
  deleteSubGroup,
  getGroups,
} from '../Service/apiService';
import toast, { Toaster } from 'react-hot-toast';

function SubGroupPage() {
  const [subGroups, setSubGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({
    subGroupName: '',
    parentGroupID: '',
    description: '',
    assignmentType: '',
  });
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    loadSubGroups();
    loadGroups();
  }, []);

  const loadSubGroups = async () => {
    try {
      const data = await getSubGroups();
      setSubGroups(data);
    } catch (err) {
      console.error("Failed to load subgroups", err);
    //  toast.error('Failed to load subgroups');
    }
  };

  const loadGroups = async () => {
    try {
      const data = await getGroups();
      setGroups(data);
    } catch (err) {
      console.error("Failed to load groups", err);
      //toast.error('Failed to load groups');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const parentGroupID = form.parentGroupID ? Number(form.parentGroupID) : null;
      const subGroupPayload = { ...form, parentGroupID };

      if (editId) {
        await updateSubGroup({ subGroupID: editId, ...subGroupPayload });
        toast.success('Level updated successfully');
      } else {
        await addSubGroup(subGroupPayload);
        toast.success('Level added successfully');
      }

      setForm({ subGroupName: '', parentGroupID: '', description: '', assignmentType: '' });
      setEditId(null);
      loadSubGroups();
    } catch (err) {
      console.error("Error saving Level", err);
      toast.error('Error saving Level');
    }
  };

  const handleEdit = (subGroup) => {
    setForm({
      subGroupName: subGroup.subGroupName || '',
      assignmentType: subGroup.assignmentType || '',
      parentGroupID: subGroup.parentGroupID ? String(subGroup.parentGroupID) : '',
      description: subGroup.description || '',
    });
    setEditId(subGroup.subGroupID);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this Level?")) {
      try {
        await deleteSubGroup(id);
        toast.success('Level deleted successfully');
        await loadSubGroups();
        // Adjust page if needed
        const totalItems = filteredSubGroups.length - 1;
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        if (currentPage > totalPages) setCurrentPage(totalPages);
      } catch (err) {
        console.error("Failed to delete Level", err);
        toast.error('Failed to delete Level');
      }
    }
  };

  // Search + Pagination
  const filteredSubGroups = subGroups.filter((subGroup) =>
    subGroup.subGroupName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredSubGroups.length / itemsPerPage);
  const paginatedSubGroups = filteredSubGroups.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="max-w-3xl mx-auto p-8">
      <Toaster position="top-right" />
      <h1 className="text-3xl font-bold mb-6">Level / Permissions</h1>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mb-8 flex flex-col space-y-4">
        <input
          type="text"
          placeholder="Level Name"
          value={form.subGroupName}
          onChange={(e) => setForm({ ...form, subGroupName: e.target.value })}
          required
          className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <select
          value={form.parentGroupID}
          onChange={(e) => setForm({ ...form, parentGroupID: e.target.value })}
          className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">-- Select Parent Group --</option>
          {groups.map((group) => (
            <option key={group.groupID} value={group.groupID}>
              {group.groupName}
            </option>
          ))}
        </select>

        <select
          value={form.assignmentType}
          onChange={(e) => setForm({ ...form, assignmentType: e.target.value })}
          className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="">-- Select Assignment Type --</option>
          <option value="Restricted">Restricted</option>
          <option value="Open">Open</option>
        </select>

        <input
          type="text"
          placeholder="Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />

        <div className="flex space-x-4">
          <button
            type="submit"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            {editId != null ? "Update" : "Add"} Level
          </button>
          {editId && (
            <button
              type="button"
              onClick={() => {
                setForm({ subGroupName: "", parentGroupID: "", description: "", assignmentType: "" });
                setEditId(null);
              }}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Search & List */}
      <div>
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search Sub Group..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#837550]"
          />
        </div>

        <ul className="space-y-4">
          {paginatedSubGroups.map((subGroup) => (
            <li
              key={subGroup.subGroupID}
              className="flex justify-between items-center border p-4 rounded shadow-sm"
            >
              <div>
                <strong className="text-lg">{subGroup.subGroupName}</strong> (Parent Group:{" "}
                {groups.find((g) => g.groupID === subGroup.parentGroupID)?.groupName || "None"})
                <div>Description: {subGroup.description}</div>
                <div className="text-sm text-gray-600">
                  Assignment: {subGroup.assignmentType || "N/A"}
                </div>
              </div>

              <div className="space-x-2">
                <button
                  onClick={() => handleEdit(subGroup)}
                  className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(subGroup.subGroupID)}
                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 border rounded ${
                  currentPage === i + 1 ? "bg-[#837550] text-white" : ""
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubGroupPage;
